import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'

import { CurrentSubView, PermissionStatus, RecognitionState } from '../../../models/enums'
import { getColorWithTransparency } from '../../../utils/colorUtils'

import Modal from '../../Common/Modal'
import * as Icons from '../../Icons/MenuIcons'
import MicrophoneModalWaiting from './MicrophoneModal/MicrophoneModalWaiting'
import MicrophoneModalRefused from './MicrophoneModal/MicrophoneModalRefused'
import MicrophoneTextBubble from './MicrophoneTextBubble'

interface MicrophoneButtonProps {
  hidden?: boolean
}

const MicrophoneButton = ({ hidden }: MicrophoneButtonProps): JSX.Element => {
  const dispatch = useDispatch()
  const isMicrophoneAllowed = useSelector((state: RootState) => state.speechReducer.isMicrophoneAllowed)
  const showMicrophoneModal = useSelector((state: RootState) => state.speechReducer.showMicrophoneModal)
  const microphonePermissionStatus = useSelector((state: RootState) => state.speechReducer.permissionStatus)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const instantIsWaitingForResponse = useSelector((state: RootState) => state.retorikReducer.instantIsWaitingForResponse)
  const fromTextToVocal = useSelector((state: RootState) => state.retorikReducer.fromTextToVocal)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)

  const [colorWithTransparency] = useState<string>(getColorWithTransparency(themeColors.secondary, 0.8))
  const [microphoneButtonClassname, setMicrophoneButtonClassname] = useState<string>()
  const [backgroundColor, setBackgroundColor] = useState<string>()
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const instantIsWaitingForResponseRef = useRef<boolean>(false)

  useEffect(() => {
    instantIsWaitingForResponseRef.current = instantIsWaitingForResponse
  }, [instantIsWaitingForResponse])

  useEffect(() => {
    let classname = 'rf-relative rf-z-[5] large:rf-h-20 rf-aspect-square rf-flex rf-justify-center rf-items-center rf-border rf-rounded-half '

    timerRef?.current && clearTimeout(timerRef.current)
    switch (activeRecognitionState) {
      case RecognitionState.Listening:
        setShowLoader(false)
        setBackgroundColor(themeColors.secondary)
        classname += `${isMobile && 'rf-h-12 rf-my-2'} rf-border-none rf-loader-pulsation`
        break
      case RecognitionState.Initializing:
      case RecognitionState.Closing:
      case RecognitionState.ForceClosing:
        setShowLoader(true)

        if (isMobile) {
          setBackgroundColor('transparent')
          classname += 'rf-h-8 rf-border-none'
        } else {
          setBackgroundColor(colorWithTransparency)
          classname += 'rf-border-truewhite'
        }
        break
      case RecognitionState.Closed:
        if (isMobile) {
          setBackgroundColor('transparent')
          classname += 'rf-h-8 rf-border-none'
        } else {
          setBackgroundColor(colorWithTransparency)
          classname += 'rf-border-truewhite'
        }

        // Timeout before setting showLoader to hide the delay between closing state and message sending
        timerRef.current = setTimeout(() => {
          setShowLoader(instantIsWaitingForResponseRef.current)
        }, 250)
        break
    }

    setMicrophoneButtonClassname(classname)
  }, [isMobile, activeRecognitionState, colorWithTransparency, instantIsWaitingForResponse])

  const handleClick = (): void => {
    if (!isMicrophoneAllowed) {
      dispatch(storeActions.speech.askPermission())
    } else {
      dispatch(storeActions.speech.toggleMicrophone(false))
    }
  }

  useEffect(() => {
    return () => {
      timerRef?.current && clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    if (fromTextToVocal) {
      handleClick()
      dispatch(storeActions.retorik.setFromTextToVocal(false))
    }
  }, [fromTextToVocal])

  useEffect(() => {
    dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
    dispatch(storeActions.utils.setFullScreenImage(undefined))
  }, [activeRecognitionState])

  return (
    <div className={`rf-relative rf-h-full rf-flex rf-justify-center rf-items-center ${hidden && 'rf-hidden'}`}>
      <button
        className={microphoneButtonClassname}
        onClick={handleClick}
        aria-label='microphone'
        style={{
          backgroundColor: backgroundColor,
          boxShadow: isMobile ? undefined : '0 0 6px #00000080'
        }}
      >
        {/* Icon or loader at the center of the button */}
        <div className='rf-absolute rf-h-full rf-w-full rf-flex rf-justify-center rf-items-center'>
          {showLoader && !isMobile ? (
            <div className='rf-w-8 rf-loader-bouncing-white' />
          ) : (
            <Icons.MicrophoneIcon
              className={isMobile ? 'rf-h-5 rf-w-5' : 'rf-h-6 rf-w-6'}
              color={!isMobile || activeRecognitionState === RecognitionState.Listening ? '#FFF' : themeColors.secondary}
            />
          )}
        </div>
      </button>

      {/* Information text box */}
      <MicrophoneTextBubble showLoader={showLoader} />

      <Modal isOpened={showMicrophoneModal} onClose={() => dispatch(storeActions.speech.setShowMicrophoneModal(false))}>
        {microphonePermissionStatus === PermissionStatus.waiting && <MicrophoneModalWaiting />}
        {microphonePermissionStatus === PermissionStatus.refused && <MicrophoneModalRefused />}
      </Modal>
    </div>
  )
}

export default MicrophoneButton
